// src/data/deutsch-schularbeit/fill-in-questions.ts
// FillIn-Übungen für Deutsch Schularbeit
// 5 Sets verknüpft mit den 5 Text-Lektionen

import { FillInQuestion } from '../../types';

// Extended interface for German course with additional fields
export interface DeutschFillInQuestion extends FillInQuestion {
  linkedLesson: string;           // ID der verknüpften Lektion
  explanation: string;            // Erklärung nach 2. Fehlversuch
  type?: 'fill_in' | 'indirect_speech';  // Lokale vs. API-Bewertung
  apiPrompt?: string;             // Prompt für Claude API (nur indirect_speech)
  expectedAnswer?: string;        // Erwartete Antwort (für indirect_speech)
  directSpeech?: string;          // Original-Satz (für indirect_speech)
}

export const fillInQuestions: DeutschFillInQuestion[] = [
  // =====================================================
  // FILLIN 1: "das oder dass?" (7 Übungen)
  // Verknüpft mit: de-lektion-dasdass
  // Bewertung: LOKAL
  // =====================================================
  {
    id: 'de-fi-01',
    category: 'dasdass',
    linkedLesson: 'de-lektion-dasdass',
    instruction: 'Setze "das" oder "dass" ein.',
    sentence: 'Die Buben stürzten sich durch _____ Buschwerk, _____ am Rand der Donau wuchs.',
    gaps: [
      { acceptedAnswers: ['das'], position: 0 },
      { acceptedAnswers: ['das'], position: 1 },
    ],
    hint: "Probier den welches-Test: Kannst du 'welches' einsetzen? Und schau ob nach der Lücke ein Nomen kommt.",
    explanation: "Lücke 1: 'das' (Artikel – steht vor dem Nomen 'Buschwerk'). Lücke 2: 'das' (Relativpronomen – '...welches am Rand der Donau wuchs' funktioniert).",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-02',
    category: 'dasdass',
    linkedLesson: 'de-lektion-dasdass',
    instruction: 'Setze "das" oder "dass" ein.',
    sentence: 'Sie spielten oft so wild, _____ es zu Verletzungen kam.',
    gaps: [{ acceptedAnswers: ['dass'], position: 0 }],
    hint: "Denk an das Muster: 'so ... ???'. Was kommt da immer?",
    explanation: "'dass' – Bindewort. Das Muster 'so ... dass' ist immer ein Bindewort mit Doppel-S.",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-03',
    category: 'dasdass',
    linkedLesson: 'de-lektion-dasdass',
    instruction: 'Setze "das" oder "dass" ein.',
    sentence: 'Die drei vereinbarten, _____ sie nichts davon sagen würden.',
    gaps: [{ acceptedAnswers: ['dass'], position: 0 }],
    hint: "Probier den welches-Test: '...vereinbarten, welches sie nichts sagen würden' – klingt das?",
    explanation: "'dass' – Bindewort. 'Welches' funktioniert hier nicht, also Bindewort mit Doppel-S.",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-04',
    category: 'dasdass',
    linkedLesson: 'de-lektion-dasdass',
    instruction: 'Setze "das" oder "dass" ein.',
    sentence: 'Einmal fanden sie ein Boot, _____ am Ufer angekettet war.',
    gaps: [{ acceptedAnswers: ['das'], position: 0 }],
    hint: "Das Wort bezieht sich zurück auf 'Boot'. Welche Wortart bezieht sich auf ein Nomen davor?",
    explanation: "'das' – Relativpronomen. Es bezieht sich auf 'Boot' und leitet einen Nebensatz ein. Probe: '...ein Boot, welches am Ufer angekettet war' ✓",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-05',
    category: 'dasdass',
    linkedLesson: 'de-lektion-dasdass',
    instruction: 'Setze "das" oder "dass" ein.',
    sentence: 'Sie rissen so heftig an der Kette, _____ sich der Pfahl dabei löste.',
    gaps: [{ acceptedAnswers: ['dass'], position: 0 }],
    hint: "Wieder das Muster! 'so heftig, ...' – was folgt immer nach 'so'?",
    explanation: "'dass' – Bindewort. Muster: 'so heftig, dass...' – immer Bindewort!",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-06',
    category: 'dasdass',
    linkedLesson: 'de-lektion-dasdass',
    instruction: 'Setze "das" oder "dass" ein.',
    sentence: 'Sie beschlossen, _____ geenterte Boot sei nun ihr Piratenboot.',
    gaps: [{ acceptedAnswers: ['das'], position: 0 }],
    hint: 'Schau was nach der Lücke kommt. Steht dort ein Nomen?',
    explanation: "'das' – Artikel. Nach der Lücke kommt 'geenterte Boot' – ein Adjektiv + Nomen. Also Artikel.",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-07',
    category: 'dasdass',
    linkedLesson: 'de-lektion-dasdass',
    instruction: 'Setze "das" oder "dass" ein.',
    sentence: 'Der Jüngste geriet unter das Boot, _____ ihn tief ins Wasser drückte.',
    gaps: [{ acceptedAnswers: ['das'], position: 0 }],
    hint: "Das Wort bezieht sich auf 'Boot' davor. Probier den welches-Test.",
    explanation: "'das' – Relativpronomen. '...das Boot, welches ihn tief ins Wasser drückte' ✓",
    difficulty: 'pflicht',
  },

  // =====================================================
  // FILLIN 2: "Aussage modifizieren" (11 Übungen)
  // Verknüpft mit: de-lektion-modifizieren
  // Bewertung: LOKAL
  // =====================================================

  // Teil A: Modalverben (7 Sätze)
  {
    id: 'de-fi-08',
    category: 'modifizieren',
    linkedLesson: 'de-lektion-modifizieren',
    instruction: 'Setze das passende Modalverb ein. (Wunsch, Absicht)',
    sentence: 'Tommy _____ in der Schule sehr cool wirken.',
    gaps: [{ acceptedAnswers: ['möchte', 'will'], position: 0 }],
    hint: 'Welches Modalverb drückt einen Wunsch aus?',
    explanation: "'möchte' oder 'will' – beide drücken einen Wunsch/eine Absicht aus.",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-09',
    category: 'modifizieren',
    linkedLesson: 'de-lektion-modifizieren',
    instruction: 'Setze das passende Modalverb ein. (Auftrag)',
    sentence: 'Das Fräulein _____ ihm einen Teller Suppe geben.',
    gaps: [{ acceptedAnswers: ['soll'], position: 0 }],
    hint: 'Welches Modalverb macht aus dem Satz einen Auftrag?',
    explanation: "'soll' – drückt einen Auftrag aus.",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-10',
    category: 'modifizieren',
    linkedLesson: 'de-lektion-modifizieren',
    instruction: 'Setze das passende Modalverb ein. (Zwang)',
    sentence: 'Er _____ noch einmal aufstehen und einen Löffel holen.',
    gaps: [{ acceptedAnswers: ['muss'], position: 0 }],
    hint: 'Welches Modalverb drückt aus, dass man keine Wahl hat?',
    explanation: "'muss' – drückt Zwang aus.",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-11',
    category: 'modifizieren',
    linkedLesson: 'de-lektion-modifizieren',
    instruction: 'Setze das passende Modalverb ein. (Verbot)',
    sentence: 'Er _____ Marceline nicht lautstark seine Meinung sagen.',
    gaps: [{ acceptedAnswers: ['darf'], position: 0 }],
    hint: "Welches Modalverb drückt ein Verbot aus? Denk daran: das 'nicht' steht schon im Satz.",
    explanation: "'darf' (nicht) – 'darf nicht' drückt ein Verbot aus.",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-12',
    category: 'modifizieren',
    linkedLesson: 'de-lektion-modifizieren',
    instruction: 'Setze das passende Modalverb ein. (Wunsch, Absicht)',
    sentence: 'Aber er _____ die Suppe wenigstens mitessen.',
    gaps: [{ acceptedAnswers: ['möchte', 'will'], position: 0 }],
    hint: 'Wieder ein Wunsch – welches Modalverb passt?',
    explanation: "'möchte' oder 'will' – Wunsch/Absicht.",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-13',
    category: 'modifizieren',
    linkedLesson: 'de-lektion-modifizieren',
    instruction: 'Setze das passende Modalverb ein. (Erlaubnis)',
    sentence: 'Bei den Spaghetti _____ er ebenfalls mitsein.',
    gaps: [{ acceptedAnswers: ['darf'], position: 0 }],
    hint: 'Welches Modalverb gibt jemandem die Erlaubnis?',
    explanation: "'darf' – drückt Erlaubnis aus.",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-14',
    category: 'modifizieren',
    linkedLesson: 'de-lektion-modifizieren',
    instruction: 'Setze das passende Modalverb ein. (Fähigkeit)',
    sentence: 'Tommy _____ nicht verstehen, warum ihn sein Gegenüber so ansieht.',
    gaps: [{ acceptedAnswers: ['kann'], position: 0 }],
    hint: 'Welches Modalverb drückt aus, ob jemand etwas schafft oder nicht?',
    explanation: "'kann' – drückt Fähigkeit aus.",
    difficulty: 'pflicht',
  },

  // Teil B: Adverbien (4 Sätze)
  {
    id: 'de-fi-15',
    category: 'modifizieren',
    linkedLesson: 'de-lektion-modifizieren',
    instruction: 'Setze das passende Adverb ein. (Vermutung)',
    sentence: 'Smilja kommt _____ erst am späten Nachmittag.',
    gaps: [{ acceptedAnswers: ['vermutlich', 'wahrscheinlich', 'vielleicht', 'möglicherweise'], position: 0 }],
    hint: 'Welches Adverb drückt aus, dass man sich nicht sicher ist?',
    explanation: "'vermutlich', 'wahrscheinlich' oder 'vielleicht' – alle drücken eine Vermutung aus.",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-16',
    category: 'modifizieren',
    linkedLesson: 'de-lektion-modifizieren',
    instruction: 'Setze das passende Adverb ein. (Verstärkung)',
    sentence: 'Smilja kommt _____ erst am späten Nachmittag.',
    gaps: [{ acceptedAnswers: ['bestimmt', 'sicher', 'definitiv', 'gewiss'], position: 0 }],
    hint: 'Welches Adverb macht die Aussage ganz sicher?',
    explanation: "'bestimmt', 'sicher' oder 'definitiv' – alle verstärken die Aussage.",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-17',
    category: 'modifizieren',
    linkedLesson: 'de-lektion-modifizieren',
    instruction: 'Setze das passende Adverb ein. (Bedauern)',
    sentence: 'Smilja kommt _____ erst am späten Nachmittag.',
    gaps: [{ acceptedAnswers: ['leider', 'bedauerlicherweise', 'unglücklicherweise'], position: 0 }],
    hint: 'Welches Adverb drückt aus, dass man etwas schade findet?',
    explanation: "'leider' oder 'bedauerlicherweise' – drückt Bedauern aus.",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-18',
    category: 'modifizieren',
    linkedLesson: 'de-lektion-modifizieren',
    instruction: 'Setze das passende Adverb ein. (Erklärung)',
    sentence: 'Smilja kommt _____ erst am späten Nachmittag.',
    gaps: [{ acceptedAnswers: ['nämlich'], position: 0 }],
    hint: "Welches Adverb leitet eine Erklärung ein – und funktioniert auch alleine, ohne vorherigen Kontext?",
    explanation: "'nämlich' – leitet eine Erklärung ein. 'Deshalb' bräuchte einen Grund, der vorher schon genannt wurde.",
    difficulty: 'pflicht',
  },

  // =====================================================
  // FILLIN 3: "Indirekte Rede" (10 Übungen)
  // Verknüpft mit: de-lektion-indirekt
  // Bewertung: CLAUDE API
  // =====================================================

  // Teil A: Brudermord im Altwasser (5 Sätze)
  {
    id: 'de-fi-19',
    category: 'indirekt',
    linkedLesson: 'de-lektion-indirekt',
    type: 'indirect_speech',
    instruction: 'Schreibe diesen Satz in die indirekte Rede um.',
    directSpeech: 'Die Mutter sagte zu den beiden Buben: „Ihr schaut ja furchterregend drein."',
    sentence: '_____',
    gaps: [{ acceptedAnswers: [], position: 0 }],
    expectedAnswer: 'Die Mutter sagte zu den beiden Buben, dass sie furchterregend drein schauen.',
    hint: "Beginne mit: 'Die Mutter sagte zu den beiden Buben, dass...'",
    explanation: "Die Mutter sagte zu den beiden Buben, dass sie furchterregend drein schauen. ('ihr' → 'sie', Aussage → 'dass')",
    apiPrompt: "Bewerte ob folgende Umwandlung in die indirekte Rede korrekt ist. Originaltext: 'Die Mutter sagte zu den beiden Buben: Ihr schaut ja furchterregend drein.' Erwartete Antwort enthält: 'dass', Pronomen 'ihr' wird zu 'sie'. Schülerantwort: {userAnswer}. Bewerte mit 0-2 Punkten. 2=perfekt, 1=kleine Fehler (z.B. Pronomen falsch aber Struktur stimmt), 0=grundlegend falsch. Gib kurzes Feedback auf Deutsch, freundlich und ermutigend. Format: PUNKTE: X/2 | FEEDBACK: ...",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-20',
    category: 'indirekt',
    linkedLesson: 'de-lektion-indirekt',
    type: 'indirect_speech',
    instruction: 'Schreibe diesen Satz in die indirekte Rede um.',
    directSpeech: 'Die Mutter fragte den Ältesten: „Wo ist euer kleiner Bruder?"',
    sentence: '_____',
    gaps: [{ acceptedAnswers: [], position: 0 }],
    expectedAnswer: 'Die Mutter fragte den Ältesten, wo ihr kleiner Bruder sei.',
    hint: "Es ist eine W-Frage. Das Fragewort bleibt! Beginne mit: 'Die Mutter fragte den Ältesten, wo...'",
    explanation: "Die Mutter fragte den Ältesten, wo ihr kleiner Bruder sei. (W-Frage → Fragewort bleibt, 'euer' → 'ihr')",
    apiPrompt: "Bewerte ob folgende Umwandlung in die indirekte Rede korrekt ist. Original: 'Die Mutter fragte den Ältesten: Wo ist euer kleiner Bruder?' Erwartete Elemente: Fragewort 'wo' bleibt, 'euer' wird zu 'ihr'. Konjunktiv I 'sei' ist Bonus. Schülerantwort: {userAnswer}. Bewerte mit 0-2 Punkten. Gib kurzes Feedback auf Deutsch, freundlich. Format: PUNKTE: X/2 | FEEDBACK: ...",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-21',
    category: 'indirekt',
    linkedLesson: 'de-lektion-indirekt',
    type: 'indirect_speech',
    instruction: 'Schreibe diesen Satz in die indirekte Rede um.',
    directSpeech: 'Die Eltern erkundigten sich ängstlich: „Ist eurem kleinen Bruder etwas passiert?"',
    sentence: '_____',
    gaps: [{ acceptedAnswers: [], position: 0 }],
    expectedAnswer: 'Die Eltern erkundigten sich ängstlich, ob ihrem kleinen Bruder etwas passiert sei.',
    hint: "Es ist eine Ja/Nein-Frage (kein Fragewort). Was brauchst du stattdessen? Beginne mit: 'Die Eltern erkundigten sich ängstlich, ob...'",
    explanation: "Die Eltern erkundigten sich ängstlich, ob ihrem kleinen Bruder etwas passiert sei. (Ja/Nein-Frage → 'ob', 'eurem' → 'ihrem')",
    apiPrompt: "Bewerte ob folgende Umwandlung in die indirekte Rede korrekt ist. Original: 'Die Eltern erkundigten sich ängstlich: Ist eurem kleinen Bruder etwas passiert?' Erwartete Elemente: 'ob' bei Ja/Nein-Frage, 'eurem' wird zu 'ihrem'. Konjunktiv I 'sei' ist Bonus. Schülerantwort: {userAnswer}. Bewerte mit 0-2 Punkten. Gib kurzes Feedback auf Deutsch, freundlich. Format: PUNKTE: X/2 | FEEDBACK: ...",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-22',
    category: 'indirekt',
    linkedLesson: 'de-lektion-indirekt',
    type: 'indirect_speech',
    instruction: 'Schreibe diesen Satz in die indirekte Rede um.',
    directSpeech: 'Der Vater schrie die Söhne an: „So macht doch endlich euren Mund auf!"',
    sentence: '_____',
    gaps: [{ acceptedAnswers: [], position: 0 }],
    expectedAnswer: 'Der Vater schrie die Söhne an, dass sie endlich ihren Mund aufmachen sollten.',
    hint: "Es ist ein Befehl. Verwende 'dass...sollten' oder einen Infinitiv mit 'zu'. Beginne mit: 'Der Vater schrie die Söhne an, ...'",
    explanation: "Der Vater schrie die Söhne an, dass sie endlich ihren Mund aufmachen sollten. (Befehl → 'dass...sollten', 'euren' → 'ihren')",
    apiPrompt: "Bewerte ob folgende Umwandlung in die indirekte Rede korrekt ist. Original: 'Der Vater schrie die Söhne an: So macht doch endlich euren Mund auf!' Es ist ein Befehl. Akzeptierte Formen: 'dass sie...sollten' oder Infinitiv mit 'zu'. 'euren' wird zu 'ihren'. Schülerantwort: {userAnswer}. Bewerte mit 0-2 Punkten. Gib kurzes Feedback auf Deutsch, freundlich. Format: PUNKTE: X/2 | FEEDBACK: ...",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-23',
    category: 'indirekt',
    linkedLesson: 'de-lektion-indirekt',
    type: 'indirect_speech',
    instruction: 'Schreibe diesen Satz in die indirekte Rede um.',
    directSpeech: 'Die Mutter fragte nach: „Habt ihr etwas zu verbergen?"',
    sentence: '_____',
    gaps: [{ acceptedAnswers: [], position: 0 }],
    expectedAnswer: 'Die Mutter fragte sie, ob sie etwas zu verbergen hätten.',
    hint: "Wieder eine Ja/Nein-Frage → 'ob'. Achte auf die Zeitform!",
    explanation: "Die Mutter fragte sie, ob sie etwas zu verbergen hätten. (Ja/Nein-Frage → 'ob', 'ihr' → 'sie')",
    apiPrompt: "Bewerte ob folgende Umwandlung in die indirekte Rede korrekt ist. Original: 'Die Mutter fragte nach: Habt ihr etwas zu verbergen?' Ja/Nein-Frage → 'ob'. 'ihr' wird zu 'sie'. Konjunktiv 'hätten' ist Bonus, 'haben' auch akzeptabel. Schülerantwort: {userAnswer}. Bewerte mit 0-2 Punkten. Gib kurzes Feedback auf Deutsch, freundlich. Format: PUNKTE: X/2 | FEEDBACK: ...",
    difficulty: 'pflicht',
  },

  // Teil B: Spaghetti für zwei (5 Sätze)
  {
    id: 'de-fi-24',
    category: 'indirekt',
    linkedLesson: 'de-lektion-indirekt',
    type: 'indirect_speech',
    instruction: 'Schreibe diesen Satz in die indirekte Rede um.',
    directSpeech: 'Tommy dachte sich: „Vielleicht hat die Schwarze kein Geld und muss schon tagelang hungern."',
    sentence: '_____',
    gaps: [{ acceptedAnswers: [], position: 0 }],
    expectedAnswer: 'Tommy dachte sich, dass die Schwarze vielleicht kein Geld habe und schon tagelang hungern müsse.',
    hint: "Es ist eine Aussage → 'dass'. Beginne mit: 'Tommy dachte sich, dass...'",
    explanation: "Tommy dachte sich, dass die Schwarze vielleicht kein Geld habe und schon tagelang hungern müsse. (Aussage → 'dass')",
    apiPrompt: "Bewerte ob folgende Umwandlung in die indirekte Rede korrekt ist. Original: 'Tommy dachte sich: Vielleicht hat die Schwarze kein Geld und muss schon tagelang hungern.' Aussage → 'dass'. Konjunktiv ist Bonus. Schülerantwort: {userAnswer}. Bewerte mit 0-2 Punkten. Gib kurzes Feedback auf Deutsch, freundlich. Format: PUNKTE: X/2 | FEEDBACK: ...",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-25',
    category: 'indirekt',
    linkedLesson: 'de-lektion-indirekt',
    type: 'indirect_speech',
    instruction: 'Schreibe diesen Satz in die indirekte Rede um.',
    directSpeech: 'Dann fragte er sich: „Würde ich mit leerem Magen ähnlich reagieren?"',
    sentence: '_____',
    gaps: [{ acceptedAnswers: [], position: 0 }],
    expectedAnswer: 'Dann fragte er sich, ob er mit leerem Magen auch so reagieren würde.',
    hint: "Eine Ja/Nein-Frage → 'ob'. 'ich' wird zu 'er'.",
    explanation: "Dann fragte er sich, ob er mit leerem Magen auch so reagieren würde. (Ja/Nein-Frage → 'ob', 'ich' → 'er')",
    apiPrompt: "Bewerte ob folgende Umwandlung in die indirekte Rede korrekt ist. Original: 'Dann fragte er sich: Würde ich mit leerem Magen ähnlich reagieren?' Ja/Nein-Frage → 'ob'. 'ich' wird zu 'er'. Schülerantwort: {userAnswer}. Bewerte mit 0-2 Punkten. Gib kurzes Feedback auf Deutsch, freundlich. Format: PUNKTE: X/2 | FEEDBACK: ...",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-26',
    category: 'indirekt',
    linkedLesson: 'de-lektion-indirekt',
    type: 'indirect_speech',
    instruction: 'Schreibe diesen Satz in die indirekte Rede um.',
    directSpeech: 'Er vermutete: „Deutsch kann sie anscheinend auch nicht."',
    sentence: '_____',
    gaps: [{ acceptedAnswers: [], position: 0 }],
    expectedAnswer: 'Er vermutete, dass sie auch kein Deutsch könne.',
    hint: "Eine Aussage → 'dass'. Beginne mit: 'Er vermutete, dass...'",
    explanation: "Er vermutete, dass sie auch kein Deutsch könne. (Aussage → 'dass')",
    apiPrompt: "Bewerte ob folgende Umwandlung in die indirekte Rede korrekt ist. Original: 'Er vermutete: Deutsch kann sie anscheinend auch nicht.' Aussage → 'dass'. Konjunktiv I 'könne' ist Bonus. Schülerantwort: {userAnswer}. Bewerte mit 0-2 Punkten. Gib kurzes Feedback auf Deutsch, freundlich. Format: PUNKTE: X/2 | FEEDBACK: ...",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-27',
    category: 'indirekt',
    linkedLesson: 'de-lektion-indirekt',
    type: 'indirect_speech',
    instruction: 'Schreibe diesen Satz in die indirekte Rede um.',
    directSpeech: 'Tommy sagte sich: „Die Fremde soll mir wenigstens die halbe Suppe zahlen."',
    sentence: '_____',
    gaps: [{ acceptedAnswers: [], position: 0 }],
    expectedAnswer: 'Tommy sagte sich, dass die Fremde ihm wenigstens die halbe Suppe zahlen solle.',
    hint: "Eine Aussage → 'dass'. 'mir' wird zu 'ihm'.",
    explanation: "Tommy sagte sich, dass die Fremde ihm wenigstens die halbe Suppe zahlen solle. (Aussage → 'dass', 'mir' → 'ihm')",
    apiPrompt: "Bewerte ob folgende Umwandlung in die indirekte Rede korrekt ist. Original: 'Tommy sagte sich: Die Fremde soll mir wenigstens die halbe Suppe zahlen.' Aussage → 'dass'. 'mir' wird zu 'ihm'. Konjunktiv I 'solle' ist Bonus. Schülerantwort: {userAnswer}. Bewerte mit 0-2 Punkten. Gib kurzes Feedback auf Deutsch, freundlich. Format: PUNKTE: X/2 | FEEDBACK: ...",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-28',
    category: 'indirekt',
    linkedLesson: 'de-lektion-indirekt',
    type: 'indirect_speech',
    instruction: 'Schreibe diesen Satz in die indirekte Rede um.',
    directSpeech: '„Vielleicht ist es in Afrika üblich, sich das Essen zu teilen?", überlegte er.',
    sentence: '_____',
    gaps: [{ acceptedAnswers: [], position: 0 }],
    expectedAnswer: 'Er überlegte, ob es vielleicht in Afrika üblich sei, sich das Essen zu teilen.',
    hint: "Eine Ja/Nein-Frage → 'ob'. Vergiss nicht 'sich das Essen' – sonst weiß man nicht, was geteilt wird!",
    explanation: "Er überlegte, ob es vielleicht in Afrika üblich sei, sich das Essen zu teilen. (Ja/Nein-Frage → 'ob')",
    apiPrompt: "Bewerte ob folgende Umwandlung in die indirekte Rede korrekt ist. Original: 'Vielleicht ist es in Afrika üblich, sich das Essen zu teilen?, überlegte er.' Ja/Nein-Frage → 'ob'. WICHTIG: 'sich das Essen zu teilen' muss vollständig sein. Konjunktiv I 'sei' ist Bonus. Schülerantwort: {userAnswer}. Bewerte mit 0-2 Punkten. Gib kurzes Feedback auf Deutsch, freundlich. Wenn 'sich das Essen' fehlt, weise darauf hin. Format: PUNKTE: X/2 | FEEDBACK: ...",
    difficulty: 'pflicht',
  },

  // =====================================================
  // FILLIN 4: "Wortproviant – Adjektive" (18 Übungen)
  // Verknüpft mit: de-lektion-adjektive
  // Bewertung: LOKAL
  // =====================================================

  // Teil A: Gegensatzpaare (10 Sätze)
  {
    id: 'de-fi-29',
    category: 'adjektive',
    linkedLesson: 'de-lektion-adjektive',
    instruction: 'Schreibe das Gegenteil.',
    sentence: 'Das Gegenteil von hochmütig ist _____.',
    gaps: [{ acceptedAnswers: ['bescheiden'], position: 0 }],
    hint: 'Hochmütig = denkt, er ist besser als alle. Was ist das Gegenteil?',
    explanation: 'bescheiden – das Gegenteil von eingebildet/hochmütig.',
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-30',
    category: 'adjektive',
    linkedLesson: 'de-lektion-adjektive',
    instruction: 'Schreibe das Gegenteil.',
    sentence: 'Das Gegenteil von langweilig ist _____.',
    gaps: [{ acceptedAnswers: ['faszinierend'], position: 0 }],
    hint: "Was ist das Gegenteil von langweilig? Es bedeutet 'total spannend'.",
    explanation: 'faszinierend',
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-31',
    category: 'adjektive',
    linkedLesson: 'de-lektion-adjektive',
    instruction: 'Schreibe das Gegenteil.',
    sentence: 'Das Gegenteil von nachgiebig ist _____.',
    gaps: [{ acceptedAnswers: ['entschlossen'], position: 0 }],
    hint: 'Nachgiebig = gibt nach. Wer das Gegenteil ist, bleibt bei seiner Entscheidung.',
    explanation: 'entschlossen – bleibt bei seiner festen Entscheidung.',
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-32',
    category: 'adjektive',
    linkedLesson: 'de-lektion-adjektive',
    instruction: 'Schreibe das Gegenteil.',
    sentence: 'Das Gegenteil von liebenswürdig ist _____.',
    gaps: [{ acceptedAnswers: ['unfreundlich'], position: 0 }],
    hint: 'Liebenswürdig = sehr nett und freundlich. Was ist das Gegenteil?',
    explanation: 'unfreundlich',
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-33',
    category: 'adjektive',
    linkedLesson: 'de-lektion-adjektive',
    instruction: 'Schreibe das Gegenteil.',
    sentence: 'Das Gegenteil von schüchtern ist _____.',
    gaps: [{ acceptedAnswers: ['selbstsicher'], position: 0 }],
    hint: 'Schüchtern = traut sich nichts. Wer das Gegenteil ist, tritt sicher auf.',
    explanation: 'selbstsicher',
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-34',
    category: 'adjektive',
    linkedLesson: 'de-lektion-adjektive',
    instruction: 'Schreibe das Gegenteil.',
    sentence: 'Das Gegenteil von mutig ist _____.',
    gaps: [{ acceptedAnswers: ['ängstlich'], position: 0 }],
    hint: 'Mutig = hat keine Angst. Das Gegenteil hat Angst.',
    explanation: 'ängstlich',
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-35',
    category: 'adjektive',
    linkedLesson: 'de-lektion-adjektive',
    instruction: 'Schreibe das Gegenteil.',
    sentence: 'Das Gegenteil von hinterhältig ist _____.',
    gaps: [{ acceptedAnswers: ['ehrlich'], position: 0 }],
    hint: 'Hinterhältig = täuscht und lügt. Was ist das Gegenteil?',
    explanation: 'ehrlich',
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-36',
    category: 'adjektive',
    linkedLesson: 'de-lektion-adjektive',
    instruction: 'Schreibe das Gegenteil.',
    sentence: 'Das Gegenteil von besonnen ist _____.',
    gaps: [{ acceptedAnswers: ['leichtsinnig'], position: 0 }],
    hint: 'Besonnen = überlegt und ruhig. Wer das Gegenteil ist, handelt ohne nachzudenken.',
    explanation: 'leichtsinnig',
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-37',
    category: 'adjektive',
    linkedLesson: 'de-lektion-adjektive',
    instruction: 'Schreibe das Gegenteil.',
    sentence: 'Das Gegenteil von arglos ist _____.',
    gaps: [{ acceptedAnswers: ['misstrauisch'], position: 0 }],
    hint: 'Arglos = vertraut jedem blind. Das Gegenteil vertraut niemandem.',
    explanation: 'misstrauisch',
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-38',
    category: 'adjektive',
    linkedLesson: 'de-lektion-adjektive',
    instruction: 'Schreibe das Gegenteil.',
    sentence: 'Das Gegenteil von fahrlässig ist _____.',
    gaps: [{ acceptedAnswers: ['vorsichtig'], position: 0 }],
    hint: 'Fahrlässig = unüberlegt, achtet nicht auf Gefahren. Das Gegenteil achtet genau.',
    explanation: 'vorsichtig',
    difficulty: 'pflicht',
  },

  // Teil B: Adjektiv → Nomen (8 Sätze)
  {
    id: 'de-fi-39',
    category: 'adjektive',
    linkedLesson: 'de-lektion-adjektive',
    instruction: 'Bilde das Nomen zum Adjektiv.',
    sentence: "Das Nomen zu 'gedankenlos' ist die _____.",
    gaps: [{ acceptedAnswers: ['Gedankenlosigkeit', 'gedankenlosigkeit'], position: 0 }],
    hint: 'Adjektive auf -los bekommen welche Endung als Nomen? -los → -losig...',
    explanation: 'Gedankenlosigkeit (-los → -losigkeit)',
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-40',
    category: 'adjektive',
    linkedLesson: 'de-lektion-adjektive',
    instruction: 'Bilde das Nomen zum Adjektiv.',
    sentence: "Das Nomen zu 'übermutig' ist der _____.",
    gaps: [{ acceptedAnswers: ['Übermut', 'übermut'], position: 0 }],
    hint: 'Diese Nomen-Bildung ist eine Sonderform – kürzer als du denkst!',
    explanation: 'Übermut (Sonderform)',
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-41',
    category: 'adjektive',
    linkedLesson: 'de-lektion-adjektive',
    instruction: 'Bilde das Nomen zum Adjektiv.',
    sentence: "Das Nomen zu 'rücksichtslos' ist die _____.",
    gaps: [{ acceptedAnswers: ['Rücksichtslosigkeit', 'rücksichtslosigkeit'], position: 0 }],
    hint: '-los → -losigkeit',
    explanation: 'Rücksichtslosigkeit (-los → -losigkeit)',
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-42',
    category: 'adjektive',
    linkedLesson: 'de-lektion-adjektive',
    instruction: 'Bilde das Nomen zum Adjektiv.',
    sentence: "Das Nomen zu 'boshaft' ist die _____.",
    gaps: [{ acceptedAnswers: ['Bosheit', 'bosheit'], position: 0 }],
    hint: '-haft Adjektive bekommen oft -heit als Nomen.',
    explanation: 'Bosheit (-haft → -heit)',
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-43',
    category: 'adjektive',
    linkedLesson: 'de-lektion-adjektive',
    instruction: 'Bilde das Nomen zum Adjektiv.',
    sentence: "Das Nomen zu 'arglos' ist die _____.",
    gaps: [{ acceptedAnswers: ['Arglosigkeit', 'arglosigkeit'], position: 0 }],
    hint: 'Wie bei gedankenlos: -los → ?',
    explanation: 'Arglosigkeit (-los → -losigkeit)',
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-44',
    category: 'adjektive',
    linkedLesson: 'de-lektion-adjektive',
    instruction: 'Bilde das Nomen zum Adjektiv.',
    sentence: "Das Nomen zu 'besonnen' ist die _____.",
    gaps: [{ acceptedAnswers: ['Besonnenheit', 'besonnenheit'], position: 0 }],
    hint: 'Achtung: Adjektive auf -en bekommen -HEIT, nicht -igkeit!',
    explanation: 'Besonnenheit (NICHT Besonnigkeit! -en → -heit)',
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-45',
    category: 'adjektive',
    linkedLesson: 'de-lektion-adjektive',
    instruction: 'Bilde das Nomen zum Adjektiv.',
    sentence: "Das Nomen zu 'fahrlässig' ist die _____.",
    gaps: [{ acceptedAnswers: ['Fahrlässigkeit', 'fahrlässigkeit'], position: 0 }],
    hint: '-ig → -igkeit',
    explanation: 'Fahrlässigkeit (-ig → -igkeit)',
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-46',
    category: 'adjektive',
    linkedLesson: 'de-lektion-adjektive',
    instruction: 'Bilde das Nomen zum Adjektiv.',
    sentence: "Das Nomen zu 'panisch' ist die _____.",
    gaps: [{ acceptedAnswers: ['Panik', 'panik'], position: 0 }],
    hint: 'Wieder eine Sonderform – das Nomen ist kürzer als das Adjektiv.',
    explanation: 'Panik (Sonderform)',
    difficulty: 'pflicht',
  },

  // =====================================================
  // FILLIN 5: "Metaphern und Redewendungen" (12 Übungen)
  // Verknüpft mit: de-lektion-metaphern
  // Bewertung: LOKAL
  // =====================================================

  // Teil A: Metaphern erklären (5 Sätze)
  {
    id: 'de-fi-47',
    category: 'metaphern',
    linkedLesson: 'de-lektion-metaphern',
    instruction: 'Ergänze die fehlende Bedeutung der Metapher.',
    sentence: "Der 'Tränensee' ist eine Metapher. Dabei wird verglichen: Tränen = große Wassermenge, so viel wie ein _____.",
    gaps: [{ acceptedAnswers: ['See', 'see'], position: 0 }],
    hint: "Welches Gewässer steckt im Wort 'Tränensee'?",
    explanation: "See – die Menge der Tränen wird mit einem See verglichen.",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-48',
    category: 'metaphern',
    linkedLesson: 'de-lektion-metaphern',
    instruction: 'Ergänze die fehlende Bedeutung der Metapher.',
    sentence: "Der 'Geistesblitz' bedeutet: ein plötzlicher kluger Einfall – schnell und hell wie ein _____.",
    gaps: [{ acceptedAnswers: ['Blitz', 'blitz'], position: 0 }],
    hint: 'Welches Naturphänomen steckt im Wort?',
    explanation: 'Blitz – ein Einfall der plötzlich kommt, wie ein Blitz.',
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-49',
    category: 'metaphern',
    linkedLesson: 'de-lektion-metaphern',
    instruction: 'Ergänze die fehlende Bedeutung der Metapher.',
    sentence: "Die 'Karriereleiter' bedeutet: der berufliche Aufstieg – man klettert hoch wie auf einer _____.",
    gaps: [{ acceptedAnswers: ['Leiter', 'leiter'], position: 0 }],
    hint: 'Worauf klettert man nach oben?',
    explanation: 'Leiter – der Aufstieg im Beruf wird mit einer Leiter verglichen.',
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-50',
    category: 'metaphern',
    linkedLesson: 'de-lektion-metaphern',
    instruction: 'Ergänze die fehlende Bedeutung der Metapher.',
    sentence: "Ein 'Ohrwurm' ist ein _____, das man nicht mehr aus dem Kopf bekommt.",
    gaps: [{ acceptedAnswers: ['Lied', 'lied', 'Song', 'song', 'Melodie', 'melodie'], position: 0 }],
    hint: 'Was hört man und es bleibt im Kopf hängen?',
    explanation: 'Ein Lied (oder eine Melodie), das man ständig im Kopf hat.',
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-51',
    category: 'metaphern',
    linkedLesson: 'de-lektion-metaphern',
    instruction: 'Ergänze die fehlende Bedeutung der Metapher.',
    sentence: "Ein 'Nesthocker' ist ein Erwachsener, der noch bei den _____ wohnt.",
    gaps: [{ acceptedAnswers: ['Eltern', 'eltern'], position: 0 }],
    hint: "Wer baut normalerweise das 'Nest' in dem man aufwächst?",
    explanation: 'Eltern – wie ein Vogel der das Nest nicht verlässt.',
    difficulty: 'pflicht',
  },

  // Teil B: Redewendungen zuordnen (4 Sätze)
  {
    id: 'de-fi-52',
    category: 'metaphern',
    linkedLesson: 'de-lektion-metaphern',
    instruction: 'Ergänze die Redewendung.',
    sentence: 'Wenn jemand angestrengt über ein Problem nachdenkt, sagt man: Er _____ sich den Kopf.',
    gaps: [{ acceptedAnswers: ['zerbricht'], position: 0 }],
    hint: 'Was macht man mit dem Kopf in dieser Redewendung?',
    explanation: "'sich den Kopf zerbrechen' = angestrengt nachdenken",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-53',
    category: 'metaphern',
    linkedLesson: 'de-lektion-metaphern',
    instruction: 'Ergänze die Redewendung.',
    sentence: 'Wenn jemand etwas zu schnell entscheidet, sagt man: Er bricht es übers _____.',
    gaps: [{ acceptedAnswers: ['Knie', 'knie'], position: 0 }],
    hint: "Über welches Körperteil wird etwas 'gebrochen'?",
    explanation: "'etwas übers Knie brechen' = übereilt entscheiden",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-54',
    category: 'metaphern',
    linkedLesson: 'de-lektion-metaphern',
    instruction: 'Ergänze die Redewendung.',
    sentence: 'Wenn jemand einen anderen verrät, sagt man: Er fällt ihm in den _____.',
    gaps: [{ acceptedAnswers: ['Rücken', 'rücken'], position: 0 }],
    hint: "In welchen Körperteil 'fällt' man jemandem?",
    explanation: "'jemandem in den Rücken fallen' = hinterrücks verraten",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-55',
    category: 'metaphern',
    linkedLesson: 'de-lektion-metaphern',
    instruction: 'Ergänze die Redewendung.',
    sentence: 'Wenn etwas gut durchdacht ist, sagt man: Das hat Hand und _____.',
    gaps: [{ acceptedAnswers: ['Fuß', 'Fuss', 'fuß', 'fuss'], position: 0 }],
    hint: 'Hand und ...? Welches andere Körperteil?',
    explanation: "'etwas hat Hand und Fuß' = ist gut durchdacht und sinnvoll",
    difficulty: 'pflicht',
  },

  // Teil C: Verdrehte Redewendungen richtigstellen (3 Sätze)
  {
    id: 'de-fi-56',
    category: 'metaphern',
    linkedLesson: 'de-lektion-metaphern',
    instruction: 'Stelle die verdrehte Redewendung richtig.',
    sentence: "Die Redewendung ist verdreht: 'Das schlägt dem Fass die Dose aus!' Richtig wäre: Das schlägt dem Fass den _____ aus!",
    gaps: [{ acceptedAnswers: ['Boden', 'boden'], position: 0 }],
    hint: 'Was hat ein Fass unten dran?',
    explanation: "'Das schlägt dem Fass den Boden aus!' = das ist unerhört/unglaublich",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-57',
    category: 'metaphern',
    linkedLesson: 'de-lektion-metaphern',
    instruction: 'Stelle die verdrehte Redewendung richtig.',
    sentence: "Die Redewendung ist verdreht: 'Hast du eine Latte vor dem Kopf!' Richtig wäre: Hast du ein _____ vor dem Kopf!",
    gaps: [{ acceptedAnswers: ['Brett', 'brett'], position: 0 }],
    hint: 'Welches Holzstück ist flach und breit?',
    explanation: "'Ein Brett vor dem Kopf haben' = etwas Offensichtliches nicht verstehen",
    difficulty: 'pflicht',
  },
  {
    id: 'de-fi-58',
    category: 'metaphern',
    linkedLesson: 'de-lektion-metaphern',
    instruction: 'Stelle die verdrehte Redewendung richtig.',
    sentence: "Die Redewendung ist verdreht: 'Du benimmst dich wie ein Esel im Porzellanladen!' Richtig wäre: Wie ein _____ im Porzellanladen!",
    gaps: [{ acceptedAnswers: ['Elefant', 'elefant'], position: 0 }],
    hint: 'Welches große, schwere Tier würde im Porzellanladen alles kaputt machen?',
    explanation: "'Wie ein Elefant im Porzellanladen' = sich grob und ungeschickt benehmen",
    difficulty: 'pflicht',
  },
];

// Helper: Get questions by linked lesson
export const getQuestionsByLesson = (lessonId: string): DeutschFillInQuestion[] => {
  return fillInQuestions.filter(q => q.linkedLesson === lessonId);
};

// Lesson order for unlocking
export const lessonOrder = [
  'de-lektion-dasdass',
  'de-lektion-modifizieren',
  'de-lektion-indirekt',
  'de-lektion-adjektive',
  'de-lektion-metaphern',
];
